import { useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const groupLabel = (kind) => (kind === "1v1" ? "Match" : kind === "team" ? "Team match" : "Pod");

export function PairingEditor({ proposal, users = {}, onPublish, onCancel }) {
  const init = {}, side0 = {};
  proposal.groups.forEach((g, i) => g.forEach((p, j) => { init[p] = i; side0[p] = j < proposal.size ? 0 : 1; }));
  proposal.byes.forEach((p) => { init[p] = "bye"; });
  const [at, setAt] = useState(init);
  const [side, setSide] = useState(side0);
  const [seq, setSeq] = useState([...proposal.groups.flat(), ...proposal.byes, ...proposal.active.filter((p) => !(p in init))]);
  const [slots, setSlots] = useState(Math.max(proposal.groups.length, 1));
  const team = proposal.kind === "team";
  const groups = Array.from({ length: slots }, (_, i) => { const g = seq.filter((p) => at[p] === i); return team ? [...g.filter((p) => !side[p]), ...g.filter((p) => side[p])] : g; });
  const byes = seq.filter((p) => at[p] === "bye" || !(p in at));
  const move = (p, v) => { setAt({ ...at, [p]: v }); setSeq([...seq.filter((x) => x !== p), p]); };
  const name = (p) => `@${users[p]?.username || p.slice(-6)}`;
  const publish = async () => {
    const bad = team && groups.findIndex((g) => g.length && (g.filter((p) => !side[p]).length !== proposal.size || g.filter((p) => side[p]).length !== proposal.size));
    if (team && bad >= 0) { toast.error(`Team match ${bad + 1} needs exactly ${proposal.size} players on Team A and ${proposal.size} on Team B`); return; }
    try { await onPublish(groups.filter((g) => g.length), byes); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-3 rounded-md border border-amber-700/40 bg-[#0B111E] p-4" data-testid="pairing-editor">
      <p className="text-sm text-amber-100">Round {proposal.round}: review pairings before publishing</p>
      <p className="text-xs text-slate-500">Move players with the menus.{team ? ` Pick each player's team (A or B); teams need ${proposal.size} players each.` : ""} Unplaced players get a bye.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {groups.map((g, i) => (
          <div key={i} className="rounded border border-[#2A3447] p-2 text-xs" data-testid={`pairing-group-${i + 1}`}>
            <p className="mb-1 font-mono text-amber-300">{groupLabel(proposal.kind)} {i + 1}</p>
            {g.map((p) => <Row key={p} p={p} side={team ? side[p] || 0 : null} onSide={(v) => setSide({ ...side, [p]: v })} name={name} value={i} slots={slots} onMove={(v) => move(p, v)} />)}
            {!g.length && <p className="text-slate-600">Empty</p>}
          </div>))}
        <div className="rounded border border-dashed border-[#2A3447] p-2 text-xs" data-testid="pairing-byes">
          <p className="mb-1 font-mono text-slate-400">Bye / unplaced</p>
          {byes.map((p) => <Row key={p} p={p} side={null} name={name} value="bye" slots={slots} onMove={(v) => move(p, v)} />)}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-outline px-3 py-1 text-xs" onClick={() => setSlots(slots + 1)} data-testid="pairing-add-group">Add {groupLabel(proposal.kind).toLowerCase()}</button>
        <button type="button" className="btn-gold px-3 py-1 text-xs" onClick={publish} data-testid="pairing-publish">Publish round</button>
        {onCancel && <button type="button" className="btn-outline px-3 py-1 text-xs" onClick={onCancel} data-testid="pairing-cancel">Cancel</button>}
      </div>
    </div>
  );
}

function Row({ p, side, onSide, name, value, slots, onMove }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      {side !== null && <select className="field w-auto py-0.5 text-xs" aria-label={`Team for ${name(p)}`} value={side} onChange={(e) => onSide(+e.target.value)} data-testid={`pairing-team-${p}`}>
        <option value={0}>A</option><option value={1}>B</option></select>}
      <span className="min-w-0 flex-1 truncate">{name(p)}</span>
      <select className="field w-auto py-0.5 text-xs" aria-label={`Move ${name(p)}`} value={value} onChange={(e) => onMove(e.target.value === "bye" ? "bye" : +e.target.value)} data-testid={`pairing-move-${p}`}>
        {Array.from({ length: slots }, (_, i) => <option key={i} value={i}>#{i + 1}</option>)}<option value="bye">Bye</option>
      </select>
    </div>
  );
}
